'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

interface AccountPreview {
  exists: boolean
  where: 'portal' | 'nextcloud' | 'both' | null
  status: 'active' | 'pending' | 'suspended' | null
  hasClub: boolean
  ncUsername: string | null
  ncDisabled: boolean
  needsClub: boolean
  needsApproval: boolean
  needsActivation: boolean
  ready: boolean
}

interface Preview {
  found: boolean
  codice?: string
  societyName?: string | null
  city?: string | null
  province?: string | null
  club?: { id: number; slug: string; name: string } | null
  account?: AccountPreview | null
}

interface Outcome {
  kind: 'created' | 'activation_requested' | 'data_completed' | 'activation_pending' | 'pending_exists' | 'already_active'
  message: string
  societyName?: string
  email?: string
  status?: string
}

const field =
  'w-full rounded-lg border border-[color:var(--league-line)] px-3 py-2 text-[14px] text-[color:var(--league-ink)] outline-none focus:border-[color:var(--league-brand)]'
const label = 'block text-[12px] font-semibold uppercase tracking-[0.12em] text-[color:var(--league-muted)]'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i

export function RegistrationForm() {
  const [form, setForm] = useState({ fipavCode: '', firstName: '', lastName: '', email: '' })
  const [preview, setPreview] = useState<Preview | null>(null)
  const [checking, setChecking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [done, setDone] = useState<Outcome | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const account = preview?.account ?? null
  const existing = Boolean(account?.exists)
  const namesRequired = !existing

  // Anteprima: società dal codice FIPAV + stato della richiesta dall'email.
  useEffect(() => {
    const codice = form.fipavCode.trim()
    const email = form.email.trim()
    setPreview(null)
    setLookupError(null)
    if (codice.length < 6) return
    const emailOk = EMAIL_RE.test(email)
    if (!emailOk && email.length > 0) return

    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setChecking(true)
      try {
        const qs = new URLSearchParams({ codice })
        if (emailOk) qs.set('email', email)
        const res = await fetch(`/api/public/v1/registration?${qs.toString()}`)
        if (res.status === 429) {
          // Rate limit: la verifica non è fallita, è solo stata troppe volte.
          setLookupError('Troppe verifiche in poco tempo. Attendi qualche minuto e riprova.')
          return
        }
        if (!res.ok && res.status !== 404) {
          // 404 = codice legittimamente non trovato (sotto); ogni altro errore
          // (429, 5xx) è un problema di verifica, non del codice inserito.
          setLookupError('Verifica del codice non disponibile in questo momento. Riprova tra poco.')
          return
        }
        const json = (await res.json()) as Preview
        setPreview(json.found ? json : { found: false })
      } catch {
        setLookupError('Verifica del codice non disponibile (errore di rete). Riprova tra poco.')
      } finally {
        setChecking(false)
      }
    }, 450)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [form.fipavCode, form.email])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const res = await fetch('/api/public/v1/registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const json = (await res.json()) as Outcome & { ok?: boolean; error?: string }
      if (!res.ok || !json.ok) {
        setError(json.error ?? 'Richiesta non riuscita. Riprova più tardi.')
        return
      }
      setDone({
        kind: json.kind,
        message: json.message ?? '',
        societyName: json.societyName ?? preview?.societyName ?? '',
        email: json.email ?? form.email,
        status: json.status,
      })
    } catch {
      setError('Errore di rete: riprova tra qualche istante.')
    } finally {
      setSaving(false)
    }
  }

  // ── Esito ──────────────────────────────────────────────────────────────────
  if (done) {
    const plain = done.kind === 'already_active' || done.kind === 'activation_pending'
    return (
      <div className={`rounded-xl border p-5 ${plain ? 'border-sky-300 bg-sky-50' : 'border-emerald-300 bg-emerald-50'}`}>
        <h2 className={`text-[20px] font-bold ${plain ? 'text-sky-900' : 'text-emerald-900'}`}>
          {done.kind === 'already_active'
            ? 'Hai già un accesso attivo'
            : done.kind === 'activation_pending'
              ? 'Richiesta già approvata'
              : done.kind === 'pending_exists'
                ? 'Richiesta già inviata'
                : 'Richiesta inviata ✔'}
        </h2>
        <p className="mt-2 text-[14px] text-[color:var(--league-ink)]">{done.message}</p>
        <p className="mt-2 text-[14px] text-[color:var(--league-muted)]">
          Email: <strong>{done.email}</strong>
          {done.societyName ? (
            <>
              {' '}
              · Società: <strong>{done.societyName}</strong>
            </>
          ) : null}
        </p>
        <Link
          href="/portal/login"
          className="mt-4 inline-block rounded-xl bg-[color:var(--league-brand-solid)] px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-[color:var(--league-brand-light)]"
        >
          Vai al login
        </Link>
      </div>
    )
  }

  const blockedByPending = Boolean(account?.status === 'pending' && !account?.needsClub)
  const blocked = Boolean(account?.ready) || Boolean(account?.needsActivation) || blockedByPending
  const canSubmit =
    !saving &&
    Boolean(preview?.found) &&
    !blocked &&
    form.email.trim().length > 0 &&
    form.fipavCode.trim().length > 0 &&
    (!namesRequired || (form.firstName.trim().length > 0 && form.lastName.trim().length > 0))

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className={label} htmlFor="fipavCode">
          Codice FIPAV della società
        </label>
        <input
          id="fipavCode"
          className={`${field} mt-1 font-mono`}
          placeholder="es. 15.073.0542"
          value={form.fipavCode}
          onChange={set('fipavCode')}
          autoComplete="off"
          required
        />
        {checking && <p className="mt-1 text-[12px] text-[color:var(--league-muted)]">Verifica in corso…</p>}
        {!checking && preview?.found && (
          <p className="mt-1 text-[12px] text-emerald-700">
            {preview.societyName}
            {preview.city ? ` — ${preview.city}` : ''}
            {preview.province ? ` (${preview.province})` : ''}
            {preview.club ? '' : ' · società non ancora censita'}
          </p>
        )}
        {!checking && preview && !preview.found && (
          <p className="mt-1 text-[12px] text-red-600">Codice non riconosciuto: controlla il codice FIPAV.</p>
        )}
        {!checking && lookupError && <p className="mt-1 text-[12px] text-amber-700">{lookupError}</p>}
      </div>

      <div>
        <label className={label} htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          className={`${field} mt-1`}
          placeholder="nome@esempio.it"
          value={form.email}
          onChange={set('email')}
          autoComplete="email"
          required
        />
        <p className="mt-1 text-[12px] text-[color:var(--league-muted)]">
          Con questa email accederai: verifica che sia corretta.
        </p>
      </div>

      {existing && (
        <div
          className={`rounded-md px-4 py-3 text-[14px] ${
            account?.ncDisabled
              ? 'border border-amber-300 bg-amber-50 text-amber-900'
              : 'border border-[color:var(--league-line)] bg-[color:var(--league-surface-alt)] text-[color:var(--league-ink)]'
          }`}
        >
          <p className="font-semibold">Risulti già presente.</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {account?.needsClub && (
              <li>
                <strong>manca il club di appartenenza</strong>: inserisci sopra il codice FIPAV della tua
                società e invia la richiesta;
              </li>
            )}
            {account?.ncDisabled && <li>il tuo accesso risulta <strong>disattivato</strong>: chiederemo alla segreteria di riattivarlo;</li>}
            {account?.status === 'pending' && <li>la tua richiesta è in attesa di approvazione;</li>}
            {account?.status === 'suspended' && <li>il profilo risulta sospeso: la segreteria valuterà la riattivazione.</li>}
            {account?.needsActivation && <li>il profilo è approvato: ti serve il link per impostare la password.</li>}
            {account?.ready && <li>l’accesso è attivo e completo.</li>}
          </ul>
        </div>
      )}

      {blocked && (
        <div className="rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-[14px] text-sky-900">
          {account?.ready ? (
            <>
              Non serve inviare nulla: puoi{' '}
              <Link href="/portal/login" className="font-semibold underline">
                accedere subito
              </Link>
              .
            </>
          ) : account?.needsActivation ? (
            <>Non serve inviare una nuova richiesta: chiedi alla segreteria di Lega il link per impostare la password.</>
          ) : (
            <>
              La richiesta per <strong>{form.email}</strong> è già in attesa di approvazione: non serve
              inviarla di nuovo.
            </>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="firstName">
            Nome{namesRequired ? '' : ' (facoltativo)'}
          </label>
          <input
            id="firstName"
            className={`${field} mt-1`}
            value={form.firstName}
            onChange={set('firstName')}
            required={namesRequired}
          />
        </div>
        <div>
          <label className={label} htmlFor="lastName">
            Cognome{namesRequired ? '' : ' (facoltativo)'}
          </label>
          <input
            id="lastName"
            className={`${field} mt-1`}
            value={form.lastName}
            onChange={set('lastName')}
            required={namesRequired}
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-[14px] text-red-700">
          <strong>Richiesta non inviata.</strong> {error}
        </div>
      )}

      {!blocked && (
        <>
          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-xl bg-[color:var(--league-brand-solid)] px-5 py-3 text-[16px] font-semibold text-white transition-colors hover:bg-[color:var(--league-brand-light)] disabled:opacity-50"
          >
            {saving ? 'Invio…' : existing ? 'Invia richiesta di abilitazione' : 'Invia la richiesta'}
          </button>
          {!canSubmit && !saving && (
            <p className="text-center text-[12px] text-amber-700">
              {checking
                ? 'Verifica del codice in corso…'
                : lookupError
                  ? 'Verifica non disponibile: riprova tra poco per poter inviare la richiesta.'
                  : form.email.trim().length > 0 && !EMAIL_RE.test(form.email.trim())
                    ? 'Inserisci un indirizzo email valido.'
                    : !preview?.found
                      ? 'Inserisci un codice FIPAV valido: senza la verifica la richiesta non può essere inviata.'
                      : !form.email.trim()
                        ? 'Inserisci la tua email per inviare la richiesta.'
                        : 'Completa nome e cognome per inviare la richiesta.'}
            </p>
          )}
          <p className="text-center text-[12px] text-[color:var(--league-muted)]">
            La password si imposta <strong>dopo l’approvazione</strong>, dal link che ricevi dalla
            segreteria di Lega.
          </p>
        </>
      )}
    </form>
  )
}
